import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';

import { CalendarEventDto } from '../../../types/calendar';
import { CreateTaskEventDto, UpdateTaskEventDto, TaskDetails } from '../../../services/task.service';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';

interface AddTaskEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateTaskEventDto | UpdateTaskEventDto) => Promise<void>;
  eventToEdit?: CalendarEventDto | null;
  loading: boolean;
  error: string | null;
  showPriority?: boolean;
  initialScheduledAt?: Date;
}

export const AddTaskEventModal: React.FC<AddTaskEventModalProps> = ({ open, onClose, onSave, eventToEdit, loading, error, showPriority = true, initialScheduledAt }) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');

  // New states for extended functionality
  const [scheduledAt, setScheduledAt] = useState<Date | null>(new Date());
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (open) {
      if (eventToEdit) {
        setTitle(eventToEdit.title);
        const details = eventToEdit.details as TaskDetails;
        setNotes(details?.notes || '');
        setPriority(details?.priority || 'medium');
        
        const eventDate = eventToEdit.scheduledLdt ? new Date(eventToEdit.scheduledLdt) : new Date();
        setScheduledAt(eventDate);
        setIsCompleted(eventToEdit.isCompleted);

        // Show picker if editing an existing event, or logic can be refined
        // For now, let's show it if it's an edit to allow changing time easily
        setShowDateTimePicker(true); 
      } else {
        setTitle('');
        setNotes('');
        setPriority('medium');
        setScheduledAt(initialScheduledAt ?? new Date());
        setIsCompleted(false);
        setShowDateTimePicker(Boolean(initialScheduledAt));
      }
    }
  }, [eventToEdit, open]);

  const handleSave = async () => {
    // If picker is shown, use selected time. Otherwise use current time.
    // If editing, and picker hidden (unlikely logic but possible), we might want to keep original time?
    // Let's stick to: if picker hidden -> use current time (create "now"), if shown -> use picked time.
    
    let finalDate = new Date();
    if (showDateTimePicker && scheduledAt) {
        finalDate = scheduledAt;
    } else if (eventToEdit && !showDateTimePicker) {
        // If editing and we hid the picker, maybe we mean "now"? Or keep original?
        // Usually "hide picker" means "I don't want to specify time", which for tasks often means "today/now" or backlog.
        // But here API requires scheduledAt.
        // Let's assume if picker is hidden during creation -> now.
        // If editing, we probably keep the picker shown by default.
        finalDate = new Date();
    }

    const taskData: CreateTaskEventDto | UpdateTaskEventDto = {
      title,
      details: {
        notes,
        priority,
      },
      scheduledLdt: toLocalIso(finalDate),
      isCompleted: isCompleted,
    };

    await onSave(taskData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-i-doc={ModalIndices.ADD_TASK_EVENT}>
      <DialogTitle>{eventToEdit ? 'Редагувати задачу' : 'Додати задачу'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          autoFocus
          margin="dense"
          label="Назва задачі"
          type="text"
          fullWidth
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={loading}
        />
        
        <TextField
          margin="dense"
          label="Нотатки"
          type="text"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
        />
        
        {showPriority && (
          <FormControl fullWidth margin="dense" variant="outlined" disabled={loading}>
            <InputLabel>Пріоритет</InputLabel>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
              label="Пріоритет"
            >
              <MenuItem value="low">Низький</MenuItem>
              <MenuItem value="medium">Середній</MenuItem>
              <MenuItem value="high">Високий</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDateTimePicker}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowDateTimePicker(checked);
                    if (checked && !scheduledAt) {
                        setScheduledAt(new Date());
                    }
                  }}
                />
              }
              label="Вказати час виконання"
            />

            {showDateTimePicker && (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
                <DateTimePicker
                  label="Запланований час"
                  value={scheduledAt}
                  onChange={(newValue) => setScheduledAt(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            )}

            {showDateTimePicker && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCompleted}
                    onChange={(e) => setIsCompleted(e.target.checked)}
                    color="primary"
                  />
                }
                label="Позначити як виконане"
              />
            )}
        </Box>

      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={loading}>Скасувати</Button>
        <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading || !title.trim() || (showDateTimePicker && !scheduledAt)}
        >
          {loading ? <CircularProgress size={24} /> : (eventToEdit ? 'Зберегти' : 'Додати')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
